import {EyeIcon} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {CustomerType} from "@/libs/enums/live";
import React from "react";
import Loader from "../Loader";

interface CampusAttendanceProps {
  isLoading: boolean;
  campusAttendance: any;
  openPopup: (title: popupParams) => void;
}

const getPercent = (value: number, total: number) =>
  total > 0 ? ((value / total) * 100).toFixed(2) : "0.00";

const PersonIcon: React.FC<{color: string; size?: number}> = ({
  color,
  size = 24
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="7.5" r="4" fill={color} />
    <path
      d="M4 20c0-4.418 3.582-7.5 8-7.5s8 3.082 8 7.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const SectionIcon: React.FC<{color: string; size?: number}> = ({
  color,
  size = 14
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2a10 10 0 0 1 0 20V2z" fill={color} />
  </svg>
);

const AttendanceRow: React.FC<{
  label: string;
  value: number;
  percent: string;
  dotColor: string;
  barBg: string;
  barColor: string;
}> = ({label, value, percent, dotColor, barBg, barColor}) => (
  <div className="flex items-center gap-3">
    <div className="flex w-[74px] shrink-0 items-center gap-1.5">
      <span
        className="h-[7px] w-[7px] shrink-0 rounded-full"
        style={{backgroundColor: dotColor}}
      ></span>
      <h5 className="text-sm font-medium text-slate-500">{label}</h5>
    </div>
    <h5 className="w-7 shrink-0 text-sm font-semibold text-slate-800">
      {value}
    </h5>
    <div className="flex flex-1 items-center gap-2">
      <div
        className="h-[6px] w-full rounded-full"
        style={{backgroundColor: barBg}}
      >
        <div
          className="h-[6px] rounded-full transition-all"
          style={{width: `${percent}%`, backgroundColor: barColor}}
        ></div>
      </div>
      <h5 className="w-14 shrink-0 text-right text-xs font-medium text-gray-500">
        {percent}%
      </h5>
    </div>
  </div>
);

const CampusAttendanceWidget: React.FC<CampusAttendanceProps> = ({
  isLoading,
  campusAttendance,
  openPopup
}) => {
  const {filterContext} = useNeoContext();
  const isEmployeeTracking =
    filterContext?.client?.customerType === CustomerType.EMPLOYEETRACKING;

  const usersPresentPercent = getPercent(
    campusAttendance.present,
    campusAttendance.total
  );
  const usersAbsentPercent = getPercent(
    campusAttendance.absent,
    campusAttendance.total
  );
  const staffPresentPercent = getPercent(
    campusAttendance.staffPresent,
    campusAttendance.staffTotal
  );
  const staffAbsentPercent = getPercent(
    campusAttendance.staffAbsent,
    campusAttendance.staffTotal
  );

  return (
    <>
      <div className="rounded-2xl border bg-neo-white ">
        <div className="p-2">
          <div className="flex items-center justify-between px-2">
            <h5 className="text-lg font-semibold">Campus Attendance</h5>
            <h5
              className="cursor-pointer pr-1"
              title="View"
              onClick={() =>
                openPopup({
                  title: isEmployeeTracking
                    ? "Employee Attendance"
                    : "Campus Attendance"
                })
              }
            >
              <EyeIcon size={18} />
            </h5>
          </div>
          {isLoading ? (
            <Loader />
          ) : (
            <div className="grid grid-cols-1 gap-4 px-2 pt-3 sm:grid-cols-[1fr_2fr]">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3 rounded-2xl bg-blue-50 p-3">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-neo-white shadow-sm">
                    <PersonIcon color="#3B82F6" size={26} />
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-slate-500">
                      Total
                      <br />
                      {isEmployeeTracking ? "Employees" : "Students"}
                    </h5>
                    <h5 className="mt-1 text-2xl font-semibold text-slate-800">
                      {campusAttendance.total}
                    </h5>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-yellow-50 p-3">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-neo-white shadow-sm">
                    <PersonIcon color="#EAB308" size={26} />
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-slate-500">
                      Total
                      <br />
                      Staff
                    </h5>
                    <h5 className="mt-1 text-2xl font-semibold text-slate-800">
                      {campusAttendance.staffTotal}
                    </h5>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-2">
                <div>
                  <div className="mb-2 flex items-center gap-1.5">
                    <SectionIcon color="#3B82F6" size={13} />
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-blue-500">
                      {isEmployeeTracking ? "Employees" : "Students"}
                    </h5>
                  </div>
                  <div className="flex flex-col gap-2">
                    <AttendanceRow
                      label="Present"
                      value={campusAttendance.present}
                      percent={usersPresentPercent}
                      dotColor="#349C42"
                      barBg="#C1E8C6"
                      barColor="#349C42"
                    />
                    <AttendanceRow
                      label="Absent"
                      value={campusAttendance.absent}
                      percent={usersAbsentPercent}
                      dotColor="#FF5555"
                      barBg="#FCD0D0"
                      barColor="#FF5555"
                    />
                  </div>
                </div>
                <hr className="my-1 border-dashed border-slate-200" />
                <div>
                  <div className="mb-2 flex items-center gap-1.5">
                    <SectionIcon color="#EAB308" size={13} />
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-yellow-500">
                      Staff
                    </h5>
                  </div>
                  <div className="flex flex-col gap-2">
                    <AttendanceRow
                      label="Present"
                      value={campusAttendance.staffPresent}
                      percent={staffPresentPercent}
                      dotColor="#349C42"
                      barBg="#C1E8C6"
                      barColor="#349C42"
                    />
                    <AttendanceRow
                      label="Absent"
                      value={campusAttendance.staffAbsent}
                      percent={staffAbsentPercent}
                      dotColor="#FF5555"
                      barBg="#FCD0D0"
                      barColor="#FF5555"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CampusAttendanceWidget;
